import { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { toast } from 'react-toastify';
import { TNoteMessage } from '../../../types';
import { DescriptionField } from '../components/NoteDescriptionField';
import { useNotesContext } from '../../../hooks/useNotesContext';

const maxFilesSize = 20971520;

type Props = {
  message?: TNoteMessage;
  onSubmit: (args: { description: string }) => Promise<any>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
};

export function MessageForm({ message, onSubmit, files, setFiles }: Props) {
  const { mediaUrls, setMediaUrls, setNoteToEdit, noteToEdit, collaborators, userId } =
    useNotesContext();
  const [descriptionError, setDescriptionError] = useState('');

  const [description, setDescription] = useState(message?.description || '');
  const [focused, setFocused] = useState(false);
  const [previewSrcs, setPreviewSrcs] = useState<
    { name?: string; lastModified?: number; src?: string }[]
  >([]);
  const [audioURLs, setAudioURLs] = useState<{ lastModified?: number; src?: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [descriptionLength, setDescriptionLength] = useState<number>(0);
  const [loadingFiles, setLoadingFiles] = useState([]);

  const inputRef = useRef(null);
  const taggingEl = useRef(null);

  const clearInput = () => {
    setDescription('');
    setFocused(false);
    setFiles([]);
    setPreviewSrcs([]);
    setAudioURLs([]);
    setIsLoading(false);
    // setHasShownError(false);
    setAddButtonDisabled(true);
    setNoteToEdit(null);
    setMediaUrls([]);
  };

  const handleClickOutside = (event) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target) &&
      taggingEl.current &&
      !taggingEl.current.contains(event.target)
    ) {
      setFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let hasErrors = false;
    if (description === '') {
      setDescriptionError('Description cannot be empty');
      hasErrors = true;
    } else {
      setDescriptionError('');
    }

    setAddButtonDisabled(hasErrors);
  }, [descriptionLength, mediaUrls, previewSrcs, audioURLs, description]);

  const handleSave = async () => {
    setAddButtonDisabled(true);
    setIsLoading(true);

    const result = await onSubmit({
      description,
    });

    if (result === 'Success') {
      setFocused(false);
      clearInput();
    } else {
      toast.error('Could not send message');
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const uploadedFiles: File[] = Array.from(event.target.files);

    if (uploadedFiles.length) {
      const fileReaders = uploadedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const { type } = file;

          setLoadingFiles((prevLoading) => [
            ...prevLoading,
            { name: file.name, lastModified: file.lastModified },
          ]);

          if (type === 'image/png' || type === 'image/jpeg' || type === 'image/webp') {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (e) => {
              resolve({
                name: file.name,
                lastModified: file.lastModified,
                src: e.target.result,
                type: 'image',
              });
            };

            reader.onerror = (e) => {
              reject(e);
            };
          } else if (type.startsWith('video/')) {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'video',
            });
          } else if (type === 'application/pdf') {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'pdf',
            });
          } else {
            toast.error('Unsupported file format');
            reject(new Error('Unsupported file format'));
          }
        });
      });

      Promise.allSettled(fileReaders).then((results) => {
        const successfulResults = results
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<{
              name: string;
              lastModified: number;
              src: string;
            }> => result.status === 'fulfilled',
          )
          .map((result) => result.value);

        const successfulFiles = uploadedFiles.filter(
          (_, index) => results[index].status === 'fulfilled',
        );

        setLoadingFiles([]);

        if (successfulResults.length) {
          setPreviewSrcs((prevSrcs) => [...prevSrcs, ...successfulResults]);
          setFiles((prevUploads) => [...prevUploads, ...successfulFiles]);
        }
      });

      event.target.value = null;
    }
  };

  const removeReview = ({ name, lastModified }: { name?: string; lastModified?: number }) => {
    const filteredPreviews = previewSrcs.filter(
      (preview) => preview.name !== name && preview.lastModified !== lastModified,
    );
    const filteredFiles = files.filter(
      (file) => file.name !== name && file.lastModified !== lastModified,
    );

    setPreviewSrcs(filteredPreviews);
    setFiles(filteredFiles);
  };

  const removeAudio = ({ lastModified }: { lastModified: number }) => {
    const filteredAudios = audioURLs.filter((audioUrl) => audioUrl.lastModified !== lastModified);
    const filteredFiles = files.filter((file) => file.lastModified !== lastModified);

    setAudioURLs(filteredAudios);
    setFiles(filteredFiles);
  };

  useEffect(() => {
    const uploadedFilesSize: number = files.reduce((acc, file) => acc + file.size, 0);

    if (files.length + mediaUrls?.length > 10) {
      setAddButtonDisabled(true);
      toast.error('Too many files, max count is 10');
    } else if (uploadedFilesSize > maxFilesSize) {
      setAddButtonDisabled(true);
      toast.error('Files are too large, max is 20mb');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const foundUser = collaborators?.find((c) => c.id === userId);

  return (
    <Box
      sx={{
        padding: '20px',
        backgroundColor: '#f2f2f2',
      }}
    >
      <DescriptionField
        focused={focused}
        setFocused={setFocused}
        foundUser={foundUser}
        isNewThread={false}
        editMode={!!noteToEdit}
        setFiles={setFiles}
        addButtonDisabled={addButtonDisabled}
        setAddButtonDisabled={setAddButtonDisabled}
        setIsLoading={setIsLoading}
        handleAdd={(updatedMessage) => onSubmit({ description: updatedMessage.description })}
        clearInput={clearInput}
        description={description}
        setDescription={setDescription}
        setAudioURLs={setAudioURLs}
        previewSrcs={previewSrcs}
        loadingFiles={loadingFiles}
        audioURLs={audioURLs}
        mediaUrls={mediaUrls}
        isLoading={isLoading}
        removeReview={removeReview}
        removeAudio={removeAudio}
        setMediaUrls={setMediaUrls}
        handleFileChange={handleFileChange}
        descriptionLength={descriptionLength}
        setDescriptionLength={setDescriptionLength}
        descriptionError={descriptionError}
      />

      {message ? (
        <Stack
          gap="10px"
          direction="row"
          mt="10px"
          sx={{
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            disabled={addButtonDisabled}
            variant="contained"
            color="primary"
            size="small"
            sx={{
              background: '#2055FF',
              color: 'white',
              font: 'Inter',
              fontWeight: '500',
              fontSize: '16px',
              paddingX: '18px',
              paddingY: '10px',
              borderRadius: '8px',
              lineHeight: '24px',
            }}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              border: '1px solid #D0D5DD',
              borderRadius: '8px',
              paddingX: '18px',
              paddingY: '10px',
              color: '#101828',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '24px',
            }}
            onClick={() => {
              clearInput();
            }}
          >
            Cancel
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
